import { useMemo } from 'react'

import { InFilterDefinition } from '@factorialco/f0-react'
import {
  SubcategoryExpenseFilterDocument,
  SubcategoryExpenseFilterQuery,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

const extractConnection = (data: SubcategoryExpenseFilterQuery) =>
  data.finance.customCategoriesConnection

const extractNodes = (connection: ReturnType<typeof extractConnection>) =>
  connection.edges.map((edge) => edge.node)

type Node = ReturnType<typeof extractNodes>[number]

export const useSubcategoryFilter = (companyId: string): InFilterDefinition<string> => {
  const source = useGraphqlDataSource(
    {
      paginationType: 'infinite-scroll',
      fetchPolicy: 'cache-first',
      document: SubcategoryExpenseFilterDocument,
      perPage: 25,
      mapData: (data) => extractNodes(extractConnection(data)),
      mapPagination: (data) => {
        const connection = extractConnection(data)
        return {
          total: connection.totalCount ?? 0,
          cursor: connection.pageInfo.endCursor ?? undefined,
          hasNextPage: !!connection.pageInfo.hasNextPage,
        }
      },
      mapVariables: ({ search }) => ({ search, companyIds: [companyId] }),
    },
    [companyId]
  )

  return useMemo(
    () => ({
      label: i18n.t('expenses_v2.forms.fields.subcategory.label'),
      type: 'in' as const,
      options: {
        source,
        mapOptions: (subcat: Node) => ({
          label: subcat.label,
          value: subcat.identifier,
        }),
      },
    }),
    [source]
  )
}
